import React from 'react';

const LAST_RELOAD_KEY = 'lazy-chunk-reload-at';
const RELOAD_COOLDOWN_MS = 60_000;

export function shouldReloadAfterChunkFailure(lastReloadAt: number | null, now: number): boolean {
  return lastReloadAt === null || now - lastReloadAt > RELOAD_COOLDOWN_MS;
}

function readLastReloadAt(): number | null {
  try {
    const raw = window.sessionStorage.getItem(LAST_RELOAD_KEY);
    return raw === null ? null : Number(raw);
  } catch {
    return null;
  }
}

function writeLastReloadAt(now: number) {
  try {
    window.sessionStorage.setItem(LAST_RELOAD_KEY, String(now));
  } catch {
    // Without storage the cooldown cannot hold, so the reload is skipped below.
  }
}

/** `React.lazy` that reloads the page once when a chunk from an earlier deploy is gone. */
export function lazyWithReload<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return React.lazy(() =>
    factory().catch((error: unknown) => {
      const now = Date.now();
      if (shouldReloadAfterChunkFailure(readLastReloadAt(), now)) {
        writeLastReloadAt(now);
        if (readLastReloadAt() === now) {
          window.location.reload();
          return new Promise<never>(() => undefined);
        }
      }
      throw error;
    })
  );
}
